import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { API_HOST } from 'appConstants';
import { getErrorText, showErrorMessage, showSuccessMessage } from 'helpers';
import axios from 'axios';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { principalIdSelector, userEmailSelector } from 'store/selectors';
import {
  accountLookup,
  saveGmailDraft,
  saveOutlookWebDraft,
  exportToWebhook,
  exportToZapier,
  exportToMake,
} from 'store/actions';
import fileDownload from 'js-file-download';
import IconButton from 'components/IconButton';
import cx from 'classnames';
import { openGoogleAuthPopup, openOutlookWebAuthPopup } from '../utils';
import './ExportModalApplication.sass';

export const ExportModalApplication = ({
  templateId,
  isLoading,
  setIsLoading,
  selectedItem,
  setSelectedItem,
}) => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const URL_REGEX =
    /^https?:\/\/(?:www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(:\d+)?(\/[^\s]*)?$/;

  const userId = useSelector(principalIdSelector);
  const userEmail = useSelector(userEmailSelector);

  const [optionalHeadersObj, setOptionalHeadersObj] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [zapierWebhookUrl, setZapierWebhookUrl] = useState('');
  const [makeWebhookUrl, setMakeWebhookUrl] = useState('');

  const APPLICATIOS_NAMES = [
    { name: 'gmail', value: 'Gmail' },
    { name: 'outlook', value: 'Outlook Web' },
    { name: 'outlookapp', value: 'Outlook App' },
    { name: 'webhook', value: 'Webhook' },
    { name: 'zapier', value: 'Zapier' },
    { name: 'make', value: 'Make' },
  ];

  const handleDownloadOutlookApp = async (type) => {
    try {
      setIsLoading(true);
      const url = `${API_HOST}/api/v2/export/mail/outlook/app/${type}/${templateId}`;
      await axios
        .get(url, {
          responseType: 'blob',
        })
        .then((res) => {
          fileDownload(res.data, `${templateId}.${type}`);

          showSuccessMessage(
            intl.formatMessage({ id: 'export-modal.success-message' }),
          );

          setIsLoading(false);
        });
    } catch {
      showErrorMessage(getErrorText());
    } finally {
      setIsLoading(false);
    }
  };

  const showApplicationName = (name, array) => {
    const found = array.find((item) => item.name === name);
    return found ? found.value : '';
  };

  return (
    <div className="export-modal-application">
      <div className="export-modal__items">
        <button
          key="gmail-application"
          disabled={isLoading && selectedItem === 'gmail'}
          onClick={async () => {
            setIsLoading(true);
            setSelectedItem('gmail');

            const payload = {
              name: userEmail,
              type: 'GMAIL',
            };

            try {
              const { authLink } = await dispatch(
                accountLookup({ userId, payload }),
              ).unwrap();

              openGoogleAuthPopup(
                authLink,
                dispatch,
                saveGmailDraft,
                templateId,
                showSuccessMessage,
                showErrorMessage,
                getErrorText,
                setIsLoading,
                setSelectedItem,
              );
            } catch {
              showErrorMessage(getErrorText());
            }
          }}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'gmail',
            active: selectedItem === 'gmail',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/gmail.svg"
            alt={`${showApplicationName('gmail', APPLICATIOS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showApplicationName('gmail', APPLICATIOS_NAMES)}
          </span>
        </button>
        <button
          key="outlook-web"
          disabled={isLoading && selectedItem === 'outlook'}
          onClick={async () => {
            setIsLoading(true);
            setSelectedItem('outlook');

            const payload = {
              name: userEmail,
              type: 'OUTLOOK',
            };

            try {
              const { authLink } = await dispatch(
                accountLookup({ userId, payload }),
              ).unwrap();

              openOutlookWebAuthPopup(
                authLink,
                dispatch,
                saveOutlookWebDraft,
                templateId,
                showSuccessMessage,
                showErrorMessage,
                getErrorText,
                setIsLoading,
                setSelectedItem,
              );
            } catch {
              showErrorMessage(getErrorText());
            }
          }}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'outlook',
            active: selectedItem === 'outlook',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/outlook-web.svg"
            alt={`${showApplicationName('outlook', APPLICATIOS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showApplicationName('outlook', APPLICATIOS_NAMES)}
          </span>
        </button>
        <button
          key="outlook-application"
          disabled={isLoading && selectedItem === 'outlookapp'}
          onClick={async () => {
            setSelectedItem('outlookapp');
          }}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'outlookapp',
            active: selectedItem === 'outlookapp',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/outlook.svg"
            alt={`${showApplicationName('outlookapp', APPLICATIOS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showApplicationName('outlookapp', APPLICATIOS_NAMES)}
          </span>
        </button>
        <button
          key="webhook-application"
          disabled={isLoading && selectedItem === 'webhook'}
          onClick={async () => setSelectedItem('webhook')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'webhook',
            active: selectedItem === 'webhook',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/webhook.svg"
            alt={`${showApplicationName('webhook', APPLICATIOS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showApplicationName('webhook', APPLICATIOS_NAMES)}
          </span>
        </button>
        <button
          key="zapier-application"
          disabled={isLoading && selectedItem === 'zapier'}
          onClick={async () => setSelectedItem('zapier')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'zapier',
            active: selectedItem === 'zapier',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/zapier.svg"
            alt={`${showApplicationName('zapier', APPLICATIOS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showApplicationName('zapier', APPLICATIOS_NAMES)}
          </span>
        </button>
        <button
          key="make-application"
          disabled={isLoading && selectedItem === 'make'}
          onClick={async () => setSelectedItem('make')}
          className={cx('export-modal__items__item', {
            loading: isLoading && selectedItem === 'make',
            active: selectedItem === 'make',
          })}>
          <img
            src="https://cdn11.waymore.io/dnd/images/export_icons/make.svg"
            alt={`${showApplicationName('make', APPLICATIOS_NAMES)}`}
          />
          <span className="export-modal-email-provider__items__item__title">
            {showApplicationName('make', APPLICATIOS_NAMES)}
          </span>
        </button>
      </div>
      <div
        className={cx('export-modal-application__details', {
          active:
            selectedItem &&
            (selectedItem === 'webhook' ||
              selectedItem === 'zapier' ||
              selectedItem === 'outlookapp' ||
              selectedItem === 'make'),
        })}>
        <h2>
          {intl.formatMessage({ id: 'export-modal.options-title' })}
          {showApplicationName(selectedItem, APPLICATIOS_NAMES)}
        </h2>
        {selectedItem === 'outlookapp' && (
          <div className="export-modal-application__details__download-btns">
            <button onClick={() => handleDownloadOutlookApp('otf')}>
              <span className="icon icon-microsoft-os" />
              <span>OTF file for Windows</span>
            </button>
            <button onClick={() => handleDownloadOutlookApp('emltpl')}>
              <span className="icon icon-mac-os" />
              <span>EMLTPL file for macOS</span>
            </button>
          </div>
        )}
        {selectedItem === 'make' && (
          <div className="export-modal-application__details__input-url">
            <label>
              <FormattedMessage id="export-modal.make.input-url" />
            </label>
            <input
              type="url"
              onChange={(e) => setMakeWebhookUrl(e.target.value)}
              name="make-url"
              placeholder="https://"
            />
          </div>
        )}
        {selectedItem === 'zapier' && (
          <div className="export-modal-application__details__input-url">
            <label>
              <FormattedMessage id="export-modal.zapier.input-url" />
            </label>
            <input
              type="url"
              onChange={(e) => setZapierWebhookUrl(e.target.value)}
              name="zapier-url"
              placeholder="https://"
            />
          </div>
        )}
        {selectedItem === 'webhook' && (
          <>
            <div className="export-modal-application__details__input-url">
              <label>
                <FormattedMessage id="export-modal.webhook.input-url" />
              </label>
              <input
                type="url"
                onChange={(e) => setWebhookUrl(e.target.value)}
                name="webhook-url"
                placeholder="https://"
              />
            </div>
            <div className="export-modal-application__details__optional-headers">
              <label>
                <FormattedMessage id="export-modal.webhook.optional-headers" />
              </label>
              {optionalHeadersObj &&
                optionalHeadersObj.map((item, index) => (
                  <div
                    key={index}
                    className="export-modal-application__details__optional-headers__row">
                    <input
                      type="text"
                      placeholder="Name"
                      name="header-name"
                      onChange={(e) => {
                        const selectedItem = optionalHeadersObj;

                        selectedItem[index].name = e.target.value;

                        setOptionalHeadersObj(selectedItem);
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      name="header-value"
                      onChange={(e) => {
                        const selectedItem = optionalHeadersObj;

                        selectedItem[index].value = e.target.value;

                        setOptionalHeadersObj(selectedItem);
                      }}
                    />
                    <IconButton
                      className="export-modal-application__details__optional-headers__row__delete-row"
                      aria-label="Delete an optional header"
                      onClick={() => {
                        setOptionalHeadersObj(
                          optionalHeadersObj.filter(
                            (item, idx) => idx !== index,
                          ),
                        );
                      }}>
                      <span className="icon icon-delete" />
                    </IconButton>
                  </div>
                ))}
              <IconButton
                className="export-modal-application__details__optional-headers__row__add-row"
                aria-label="Add an optional header"
                onClick={() =>
                  optionalHeadersObj.length < 4
                    ? setOptionalHeadersObj([
                        ...optionalHeadersObj,
                        {
                          name: '',
                          value: '',
                        },
                      ])
                    : showErrorMessage(
                        'Maximum optional headers length exceeded',
                      )
                }>
                <span className="icon icon-add" />
              </IconButton>
            </div>
          </>
        )}
        <div className="export-modal-application__details__actions">
          {selectedItem === 'webhook' && (
            <button
              disabled={isLoading || !webhookUrl}
              onClick={async () => {
                if (!URL_REGEX.test(webhookUrl)) {
                  showErrorMessage(
                    intl.formatMessage({ id: 'export-modal.url-validation' }),
                  );
                } else {
                  try {
                    const res = await dispatch(
                      exportToWebhook({
                        templateId,
                        webhookUrl,
                        webhookHeaders: optionalHeadersObj,
                      }),
                    );

                    if (res.meta.requestStatus === 'fulfilled') {
                      showSuccessMessage(
                        'Your email template has been successfully exported to the Webhook Url!',
                      );
                    }
                  } catch {
                    showErrorMessage(getErrorText());
                  } finally {
                    setIsLoading(false);
                    setSelectedItem('');
                  }
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'zapier' && (
            <button
              disabled={isLoading || !zapierWebhookUrl}
              onClick={async () => {
                if (!URL_REGEX.test(zapierWebhookUrl)) {
                  showErrorMessage(
                    intl.formatMessage({ id: 'export-modal.url-validation' }),
                  );
                } else {
                  try {
                    const res = await dispatch(
                      exportToZapier({
                        templateId,
                        zapierWebhookUrl,
                      }),
                    );

                    if (res.meta.requestStatus === 'fulfilled') {
                      showSuccessMessage(
                        'Your email template has been successfully exported to the Zapier webhook Url!',
                      );
                    }
                  } catch {
                    showErrorMessage(getErrorText());
                  } finally {
                    setIsLoading(false);
                    setSelectedItem('');
                  }
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
          {selectedItem === 'make' && (
            <button
              disabled={isLoading || !makeWebhookUrl}
              onClick={async () => {
                if (!URL_REGEX.test(makeWebhookUrl)) {
                  showErrorMessage(
                    intl.formatMessage({ id: 'export-modal.url-validation' }),
                  );
                } else {
                  try {
                    const res = await dispatch(
                      exportToMake({
                        templateId,
                        makeWebhookUrl,
                      }),
                    );

                    if (res.meta.requestStatus === 'fulfilled') {
                      showSuccessMessage(
                        'Your email template has been successfully exported to the Make webhook Url!',
                      );
                    }
                  } catch {
                    showErrorMessage(getErrorText());
                  } finally {
                    setIsLoading(false);
                    setSelectedItem('');
                  }
                }
              }}>
              <span className="icon icon-cloud-download" />
              <FormattedMessage id="button.export" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ExportModalApplication.propTypes = {
  templateId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  selectedItem: PropTypes.string,
  setSelectedItem: PropTypes.func.isRequired,
};

export default ExportModalApplication;
